"use client";

import React, { useState, useRef } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";

interface SimpleTableHeaderSearchBarProps {
  entity: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
}

/**
 * Search bar component for SimpleTableHeader.
 * Renders as a compact "Search" icon+label when idle.
 * Expands to a full input on click, and collapses back on blur when empty.
 */
export function SimpleTableHeaderSearchBar({
  entity,
  searchValue,
  onSearchChange,
}: SimpleTableHeaderSearchBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Entity-specific character limits
  const charLimitMap: Record<string, number> = {
    communications: 100,
    deals: 50,
  };
  const charLimit = charLimitMap[entity] || 20;
  const placeholder =
    entity === "communications"
      ? "Search activities..."
      : entity === "deals"
        ? "Search deals..."
        : `Search ${entity}...`;
  const title =
    entity === "communications"
      ? "Search activities (up to 100 characters)"
      : entity === "deals"
        ? "Search deals (up to 50 characters)"
        : `Search limited to ${charLimit} characters`;

  const showExpanded = isExpanded || searchValue.length > 0;

  const handleExpand = () => {
    setIsExpanded(true);
    // Defer focus so the input is mounted/visible first
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const handleBlur = () => {
    if (!searchValue) {
      setIsExpanded(false);
    }
  };

  if (!showExpanded) {
    return (
      <button
        type="button"
        onClick={handleExpand}
        className="flex items-center gap-1.5 h-8 px-2.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-all whitespace-nowrap"
        title="Click to search"
      >
        <Search className="h-3.5 w-3.5 flex-shrink-0" />
        <span>Search</span>
      </button>
    );
  }

  return (
    <div className="relative flex-1 max-w-xs">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5 pointer-events-none" />
      <Input
        ref={inputRef}
        placeholder={placeholder}
        value={searchValue}
        onChange={(e) => {
          const value = e.target.value;
          if (value.length <= charLimit) {
            onSearchChange(value);
          }
        }}
        onBlur={handleBlur}
        maxLength={charLimit}
        className="pl-8 pr-8 h-8 text-xs"
        title={title}
      />
      {searchValue && (
        <button
          onClick={() => onSearchChange("")}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
          type="button"
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
